import type { ComponentType } from 'react';
import type { MDXComponents } from 'mdx/types';
import { lazyComponent, registerPreload } from '../lib/lazy';
import type { PaintingName } from '../components/Painting';
import { TUTORIAL_SECTIONS, TUTORIAL_TOPICS, tutorialPath } from './tutorials';
import type { TutorialSection, TutorialTopic } from './tutorials';

/** Sections and topics live in ./tutorials so Node can read them too (routes, sitemap). */
export * from './tutorials';

/**
 * Every MDX page and post. Routes, the blog index and the prerender list all
 * come from these files' frontmatter (the node side reads the same
 * frontmatter in vite-plugins/content.ts).
 *
 * Frontmatter is read up front through `?meta` (vite-plugins/mdx-meta.ts), and
 * each body is its own chunk, loaded by the page that shows it. Before
 * rendering a route, entry-server.tsx and main.tsx call preloadRoute
 * (src/lib/lazy.ts), so the prerender and the hydration both see the body
 * already loaded and never suspend.
 *
 * readMinutes and toc are added by vite-plugins/remark-statio.ts.
 */

type Content = ComponentType<{ components?: MDXComponents }>;
type Load = () => Promise<Content>;

const slugOf = (file: string) => file.replace(/^.*\/|\.mdx$/g, '');

/** Joins each file's frontmatter to a lazy Content for its body, and registers the route's preload. */
function entries<F>(
  meta: Record<string, F>,
  bodies: Record<string, Load>,
  pathOf: (file: string, fm: F) => string,
): (F & { slug: string; Content: Content })[] {
  return Object.entries(meta).map(([file, fm]) => {
    const { Component: Content, preload } = lazyComponent(bodies[file]);
    registerPreload(pathOf(file, fm), preload);
    return { ...fm, slug: slugOf(file), Content };
  });
}

export interface TocItem {
  id: string;
  text: string;
}

export interface PageFrontmatter {
  /** Route, without a trailing slash: /mcp-permissions */
  path: string;
  /** The <title>. */
  title: string;
  /** Meta description, also the social card text. */
  description: string;
  /** The h1. */
  heading: string;
  lede?: string;
  art?: PaintingName;
  /** Optional button under the lede. */
  action?: { label: string; href: string };
}

export interface PostFrontmatter {
  title: string;
  description: string;
  /** ISO date in quotes, "YYYY-MM-DD". The scheduled publisher reads this line. */
  date: string;
  /** Last real change to the post, "YYYY-MM-DD". Sitemap lastmod; defaults to date. */
  updated?: string;
  author: string;
  /** Card and social image under public/, 1200x630. */
  image: string;
  imageAlt: string;
  lede?: string;
  readMinutes: number;
  toc: TocItem[];
}

export interface TutorialFrontmatter {
  title: string;
  description: string;
  /** ISO date in quotes, "YYYY-MM-DD". */
  date: string;
  /** Last time the steps were re-run, "YYYY-MM-DD". Defaults to date. */
  updated?: string;
  author: string;
  topic: TutorialTopic;
  /** Position inside its topic in the sidebar, lowest first. */
  order: number;
  /** What the steps were last run against, e.g. "Claude Code 2.1 on macOS 15". */
  tested: string;
  lede?: string;
  readMinutes: number;
  toc: TocItem[];
}

/**
 * A write-up for one catalog server, at src/content/servers/<slug>.mdx, where
 * <slug> is the server's slug in src/catalog/catalog.json. Having one is what
 * opens /mcp-servers/<slug> to search engines.
 */
export interface ServerGuideFrontmatter {
  /** Optional <title> without the suffix. Defaults to "<name> MCP server". */
  title?: string;
  /** Optional meta description. Defaults to the catalog description. */
  description?: string;
  /** ISO date in quotes, "YYYY-MM-DD": the last time the facts were checked. */
  updated: string;
  /** What the tool list and steps were checked against, e.g. "mcp-atlassian 2.0.1, Claude Code 2.1". */
  tested: string;
  readMinutes: number;
  toc: TocItem[];
}

export type Page = PageFrontmatter & { Content: Content };
export type Post = PostFrontmatter & { slug: string; Content: Content };
export type Tutorial = TutorialFrontmatter & { slug: string; Content: Content };

export const postPath = (slug: string) => `/blog/${slug}`;

export const PAGES: Page[] = entries(
  import.meta.glob<PageFrontmatter>('./pages/*.mdx', { eager: true, query: '?meta', import: 'frontmatter' }),
  import.meta.glob<Content>('./pages/*.mdx', { import: 'default' }),
  (_, fm) => fm.path,
);

/** Newest first. */
export const POSTS: Post[] = entries(
  import.meta.glob<PostFrontmatter>('./blog/*.mdx', { eager: true, query: '?meta', import: 'frontmatter' }),
  import.meta.glob<Content>('./blog/*.mdx', { import: 'default' }),
  (file) => postPath(slugOf(file)),
).sort((a, b) => b.date.localeCompare(a.date));

const topicOrder = (t: TutorialTopic) => TUTORIAL_TOPICS.findIndex((x) => x.id === t);

/** In sidebar order: by topic, then by `order` inside it. */
export const TUTORIALS: Tutorial[] = entries(
  import.meta.glob<TutorialFrontmatter>('./tutorials/*.mdx', { eager: true, query: '?meta', import: 'frontmatter' }),
  import.meta.glob<Content>('./tutorials/*.mdx', { import: 'default' }),
  (file) => tutorialPath(slugOf(file)),
).sort((a, b) => topicOrder(a.topic) - topicOrder(b.topic) || a.order - b.order);

export type ServerGuide = ServerGuideFrontmatter & { Content: Content };

/** Server guides by catalog slug. */
export const SERVER_GUIDES: Record<string, ServerGuide> = Object.fromEntries(
  entries(
    import.meta.glob<ServerGuideFrontmatter>('./servers/*.mdx', { eager: true, query: '?meta', import: 'frontmatter' }),
    import.meta.glob<Content>('./servers/*.mdx', { import: 'default' }),
    // The catalog's own path, written out so this module doesn't import the catalog.
    (file) => `/mcp-servers/${slugOf(file)}`,
  ).map(({ slug, ...g }) => [slug, g]),
);
/** The tutorials in one section, in sidebar order. */
export const tutorialsInSection = (section: TutorialSection) =>
  TUTORIALS.filter((t) => section.topics.includes(t.topic));

/** The tutorials under one topic, in sidebar order. */
export const tutorialsInTopic = (topic: TutorialTopic) => TUTORIALS.filter((t) => t.topic === topic);

/** Sections that have something in them: an empty section gets no hub, no route and no sidebar line. */
export const LIVE_SECTIONS = TUTORIAL_SECTIONS.filter((s) => tutorialsInSection(s).length > 0);

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${months[m - 1]} ${d}, ${y}`;
}
